import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import Table from 'UI/Table'
import SubMenu from 'FoundationalComponents/AssetSubMenus/Host'
import VcenterSubMenu from 'FoundationalComponents/AssetSubMenus/Vcenter'
import BooleanIcon from 'UI/BooleanIcon'
import RAGIcon from 'FoundationalComponents/RAGIcon'
import { find, isNil, map, compact, uniq, sortBy  } from 'lodash'

import { HostReportFilters as Filters, filteredData, booleanThFilter } from './Filters';

const clusterLabel = (value) => isNil(value) ? 'No cluster information' : value
const HealthState = ({ healthState, inMaintenanceMode }) => {
  let color = 'red'
  let icon = 'heart-rate'
  let title = healthState
  switch (healthState) {
    case 'Normal':
      color = 'green'
      break;
    case 'Warning':
      color = 'amber'
      break;
    case null:
      color = 'grey'
      title = 'Unknown'
      break;
    case undefined:
      color = 'grey'
      title = 'Unknown'
      break;
    case '':
      color = 'grey'
      title = 'Unknown'
      break;
    default:
      color = 'red'
  }

  if (inMaintenanceMode) {
    color = 'grey'
    title = 'In Maintenance Mode'
    icon = 'wrench'
  }

  return <RAGIcon {...{ color, title, icon, size: 'big' }} />
}

const inverseTrueIcon = { icon: 'check', type: 'solid', text: '', color: 'red' }
const inverseFalseIcon = { icon: 'times', type: 'solid', text: '', color: 'green' }

const VcenterAssetReportHosts = ({ hosts, locations }) => {
  const [filters, changeFilters] = useState({
    locations: 'all',
    cluster: 'all',
    powerState: 'all' ,
    esxi_password_complexity: 'all',
    vmotion_enabled: 'all',
    dcui_running: 'all',
    shell_running: 'all',
    ntp_running: 'all',
    ntp_configured: 'all',
    snmp_running: 'all',
    ssh_running: 'all',
    mondoo_active: 'all',
    mondoo_vulnerabilities: 'all',
  })

  const filterOptions = {
    locations: map(compact(locations), ({ id, name }) => ({ label: name, value: id })),
    cluster: map(sortBy(uniq(map(hosts, 'cluster'))), (cluster) => ({ label: clusterLabel(cluster), value: cluster })),
  }

  const columns = [
    { Header: 'Name', accessor: 'name', width: 200, Cell: ({ original }) => (
      <>
        <SubMenu host={original} />
        <i style={{ fontSize: 'small' }}>{original.asset_label}</i>
      </>
    ) },
    {
      Header: 'Status',
      accessor: 'power_state',
      width: 110,
      filterable: false,
      Cell: (data) => (
        <span style={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <BooleanIcon {...{ booleanValue: data.value === 'On', trueIcon: { icon: 'power-off' }, falseIcon: { icon: 'power-off' } }} />

          <HealthState healthState={data.original.overall_health_state} inMaintenanceMode={data.original.in_maintenance_mode} />
        </span>
      )
    },
    {
      Header: 'Location',
      id: 'location_name',
      type: 'list',
      width: 230,
      sortMethod: (a, b) => {
        const location_a = find(a, ['label', 'Location'])
        const location_b = find(b, ['label', 'Location'])

        return (location_a.value > location_b.value ? 1 : -1)
      },
      accessor: (data) => ([
        { label: 'Location', value: data.location_name },
        { label: 'Cluster', value: data.cluster },
        { label: 'vCenter', value: <VcenterSubMenu vcenter={data.vcenter} /> }
      ])
    },
    {
      Header: 'Hardware Model',
      type: 'list',
      width: 230,
      id: 'hardware_model',
      sortMethod: (a, b) => {
        const system_a = find(a, ['label', null])
        const model_a = find(a, ['label', 'Model'])
        const system_b = find(b, ['label', null])
        const model_b = find(b, ['label', 'Model'])

        return (system_a.value == system_b.value ? model_a.value > model_b.value ? 1 : -1 : system_a.value > system_b.value ? 1 : -1)
      },
      accessor: (data) => ([
        { label: null, value: data.hardware_system },
        { label: 'Model', value: data.hardware_model },
        { label: 'Type', value: data.hardware_type },
        { label: 'BIOS Version', value: data.bios_version },
        { label: 'Serial number', value: data.serial_number }
      ])
    },
    { Header: 'CPU', accessor: 'num_cpu', type: 'number', width: 120 },
    { Header: 'CPU Sockets', accessor: 'cpu_sockets', type: 'number', width: 120 },
    { Header: 'Memory', accessor: 'memory_total_gb', type: 'humanizeGB', width: 120 },
    { Header: 'Syslog Hosts', accessor: 'syslog_hosts', width: 255, Cell: (row) => {
      const hosts = String(row.value).split(',');
      return (
        <ul>
          {map(hosts, host => (
            <li> {host} </li>
          ))}
        </ul>
      )
    }},
    { Header: 'ESXI Version', accessor: 'esxi_version', width: 120 },
    { Header: 'Platform EOL', accessor: 'platform_eol_check', Cell: (row) => {
      return (
        <div style={{ color: row.value === 180 ? 'green' : row.value === 30 ? 'orange' : 'red', textAlign: 'center' }}>
          {row.value != 0 && '>'} {row.value + ' days'}
        </div>
      )
     } },
    { Header: 'ESXI Password Complexity', accessor: 'esxi_password_complexity', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'esxi_password_complexity' })},
    { Header: 'vmfs_ats_heartbeat', accessor: 'vmfs_ats_heartbeat', width: 90, type: 'number', Cell: (row) => {
      return (
        <div style={{ color: row.value === 1 ? 'green' : row.value === 0 ? 'red' : 'orange', textAlign: 'center'}}>
          {row.value === 0 ? 'Disabled' : row.value === 1 ? 'Enabled' : 'Unknown'}
        </div>
      )
    } },
    { Header: 'data_mover_max_transfer_size', accessor: 'data_mover_max_transfer_size', width: 90, type: 'number', Cell: (row) => {
      return (
        <div style={{textAlign: 'center'}}>
          {row.value}
        </div>
      )
    } },
    { Header: 'vMotion', accessor: 'vmotion_enabled', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'vmotion_enabled' }) },
    { Header: 'DCUI', accessor: 'dcui_running', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'dcui_running' }) },
    { Header: 'Shell', accessor: 'shell_running', falseIcon: inverseFalseIcon, trueIcon: inverseTrueIcon, width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'shell_running' }) },
    { Header: 'NTP', accessor: 'ntp_running', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'ntp_running' }) },
    { Header: 'NTP Configured', accessor: 'ntp_configured', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'ntp_configured' })},
    { Header: 'NTP Servers', accessor: 'ntpserver', width: 180, Cell: (row) => {
      const ntpservers = row.value?.replaceAll('[', '').replaceAll('"', '').replaceAll(']','').split(',');
      return (
        <ul>
          {map(ntpservers, ntpserver => (
            <li> {ntpserver} </li>
          ))}
        </ul>
      )
    }},
    { Header: 'SNMP', accessor: 'snmp_running', width: 90, type: 'boolean', falseIcon: inverseFalseIcon, trueIcon: inverseTrueIcon, Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'snmp_running' }) },
    { Header: 'SSH', accessor: 'ssh_running', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'ssh_running' }) },
    { Header: 'Mondoo', accessor: 'mondoo_active', width: 90, type: 'boolean', Filter: ({ filter }) => booleanThFilter({ filter, changeFilters, filters, filterName: 'mondoo_active' }) },
    { Header: 'Mondoo Vulnerabilities', accessor: 'mondoo_vulnerabilities', type: 'number', width: 120, Cell: (row) => {
      return (
        <div style={{ color: row.value === 0 ? 'green' : 'red', textAlign: 'center ' }}>
          {row.value}
        </div>
      )
    } },
  ]

  return (
    <Fragment>
      <Filters {...{ filters, changeFilters, filterOptions }} />

      <Table {...{
        data: filteredData({ hosts, filters }),
        columns: columns,
        tableProps: {
          defaultSorted: [{ id: 'name', asc: true }],
          filterable: true
        }
      }} />
    </Fragment>
  )
}

export default VcenterAssetReportHosts
